/**
 * Connections list page
 * Grid of database connection cards with CRUD operations
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RefreshCw } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ConnectionCard } from '@/components/discovery/connection-card'
import {
  useConnections,
  useTestConnection,
  useDeleteConnection,
  useStartScan,
} from '@/hooks/use-discovery'
import type { DataConnection, ConnectionQueryParams, DatabaseType } from '@/types/discovery'

const DEFAULT_PAGE_SIZE = 12

export function DiscoveryConnectionsPage() {
  const navigate = useNavigate()

  const [filters, setFilters] = useState<ConnectionQueryParams>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<DataConnection | null>(null)

  const { data, isLoading, refetch } = useConnections({
    ...filters,
    search: search || undefined,
  })

  const testConnection = useTestConnection()
  const deleteConnectionMutation = useDeleteConnection()
  const startScanMutation = useStartScan()

  const connections = data?.data ?? []
  const pagination = data?.pagination

  const handleTest = useCallback(
    (connection: DataConnection) => {
      testConnection.mutate(connection.id)
    },
    [testConnection]
  )

  const handleScan = useCallback(
    (connection: DataConnection) => {
      startScanMutation.mutate({ connectionId: connection.id })
    },
    [startScanMutation]
  )

  const handleClick = useCallback(
    (connection: DataConnection) => {
      navigate(`/discovery/connections/${connection.id}`)
    },
    [navigate]
  )

  const handleEdit = useCallback(
    (connection: DataConnection) => {
      navigate(`/discovery/connections/${connection.id}`)
    },
    [navigate]
  )

  const handleDelete = useCallback(
    (connection: DataConnection) => {
      setDeleteTarget(connection)
    },
    []
  )

  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      deleteConnectionMutation.mutate(deleteTarget.id)
    }
    setDeleteTarget(null)
  }, [deleteTarget, deleteConnectionMutation])

  const handleTypeFilter = useCallback(
    (value: string) => {
      setFilters({
        ...filters,
        page: 1,
        databaseType: value === 'all' ? undefined : (value as DatabaseType),
      })
    },
    [filters]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ ...filters, page: newPage })
    },
    [filters]
  )

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Applications</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            <span className="sr-only">Refresh</span>
          </Button>
          <Button
            size="sm"
            className="h-8"
            onClick={() => navigate('/discovery/connections/new')}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            New Application
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage application connections for data discovery and classification.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search applications..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 max-w-xs"
          />
          <Select
            value={filters.databaseType ?? 'all'}
            onValueChange={handleTypeFilter}
          >
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="Database type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="POSTGRESQL">PostgreSQL</SelectItem>
              <SelectItem value="MSSQL">SQL Server</SelectItem>
              <SelectItem value="SNOWFLAKE">Snowflake</SelectItem>
              <SelectItem value="BIGQUERY">Google BigQuery</SelectItem>
              <SelectItem value="REDSHIFT">Amazon Redshift</SelectItem>
              <SelectItem value="DATABRICKS">Databricks</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-48 animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && connections.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              {search || filters.databaseType
                ? 'No applications found matching your filters.'
                : 'No applications yet. Add an application to get started.'}
            </p>
            {!search && !filters.databaseType && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/discovery/connections/new')}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Application
              </Button>
            )}
          </div>
        )}

        {/* Connections grid */}
        {!isLoading && connections.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {connections.map((connection) => (
                <ConnectionCard
                  key={connection.id}
                  connection={connection}
                  onTest={handleTest}
                  onScan={handleScan}
                  onClick={handleClick}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isTestPending={testConnection.isPending}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {connections.length} of {pagination.totalItems}{' '}
                  application{pagination.totalItems === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{deleteTarget?.name}&quot; and
              all associated scan data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
